
<script setup lang="ts">
import type {Cart, CartProduct} from "~/utils/Cart";
import {isPriceOfProductVisible, Product} from "~/utils/Product";
import {useRouter} from "vue-router";
import type {UnwrapRef} from "vue";
import type {DataTableRowReorderEvent} from "primevue/datatable";
import InputText from "primevue/inputtext";
import {Article} from "~/utils/Article";
import type {AutoCompleteItemSelectEvent} from "primevue/autocomplete";
import {useModyApi} from "~/middelware/ModyApi";
import draggable from "vuedraggable";
import type {MyEvent} from "~/utils/Event";
import { useMyFunctions } from "~/middelware/MyFunctions";
import { useWindowSize } from "@vueuse/core";

const router = useRouter()
const api = useModyApi()
const myFunctions = useMyFunctions()
const properties = defineProps<{
  cartProducts:CartProduct[],
  event?:MyEvent,
  loaded:Boolean
}>()
let editSectionName:Boolean[] = []

const emit = defineEmits<{
  (e:"removeProduct", index:number):void,
  (e:"updateCart"):void,
}>()
const productList = ref<Product[]>([])

async function removeProduct(index:number){
  emit("removeProduct", index)
}
async function updateCart(){
  emit("updateCart")
}

function getTotalPrice(){
  if(properties.event){
    return properties.event.getNettoSum()
  }
  else{
    let total = 0;
    properties.cartProducts.forEach((product)=>{
      total += product.price * product.count * product.factor
    })
    return total
  }
}

function getNettoSumDiscounted(){
  if(properties.event){
    return properties.event.getNettoSumDiscounted()
  }
  else{
    return getTotalPrice()
  }
}
function getTax(){
  return getNettoSumDiscounted()*0.2
}
function getTotalInclTax(){
  return getNettoSumDiscounted() * 1.2
}

function onDragEnded(evt:CustomEvent){
  updateCart()
}

const showAddProductRow = ref(false)
const newProduct = ref<Product>(new Product())
function addProduct(){
  showAddProductRow.value = true
}
function cancelAddProduct(){
  showAddProductRow.value = false
  newProduct.value = new Product()
}
async function saveNewProduct(){
  properties.cartProducts.push({count:1,product:newProduct.value, factor:1.0, price:newProduct.value.Price, comment:"",sectionName:"",editSectionName:false})
  showAddProductRow.value = false
  newProduct.value = new Product()
  await updateCart()
}
function saveSectionName(product:CartProduct){
  product.editSectionName = false
  emit("updateCart")
}

async function loadAutocompleteProducts(){
  productList.value = await api.searchProductsByName(newProduct.value.Name)
}
function setProduct(event: AutoCompleteItemSelectEvent){
  newProduct.value = event.value
}

const containsProductWithInvisiblePrice = computed(()=>{
  let containsInvisiblePrice = false
  properties.cartProducts.forEach((product)=>{
    if(!isPriceOfProductVisible(product.product)){
      containsInvisiblePrice = true
    }
  })
  return containsInvisiblePrice
})
function refreshPrices(){
  properties.cartProducts.forEach((product)=>{
    if(isPriceOfProductVisible(product.product)){
      product.price = product.product.Price
    }
  })
}
</script>
<template>
  <div class="cartTable">
    <div class="cartTableHeader" v-if="!myFunctions.isMobile.value">
      <div class="cartTableHeaderColumn" v-if="!myFunctions.isMobile.value"></div>
      <div class="cartTableHeaderColumn"></div>
      <div class="cartTableHeaderColumn"></div>
      <div class="cartTableHeaderColumn">Name</div>
      <div class="cartTableHeaderColumn">Anzahl</div>
      <div class="cartTableHeaderColumn" v-if="!myFunctions.isMobile.value">
        Einzelpreis 
        <Button v-if="event" @click="refreshPrices" icon="pi pi-refresh" variant="text" rounded aria-label="Filter"></Button>
      </div>
      <div class="cartTableHeaderColumn">Gesamtpreis</div>
    </div>
    <draggable :list="cartProducts" handle=".handle" @end="onDragEnded" item-key="id">
      <template #item="{element:product, index}">
       <div>
        <template v-if="event">
         <div v-if="product.editSectionName" style="display:inline;">
           <InputText  v-model="product.sectionName"></InputText>
           <Button text @click="saveSectionName(product)">Done</Button>
         </div>
         <div class="sectionName" v-else :class="{background:product.sectionName != ''}">
           <Button text @click="product.editSectionName = !product.editSectionName">
             <template v-if="product.sectionName == ''">+</template>
             <template v-else>{{ product.sectionName }}</template>
           </Button>
         </div>
        </template>
        <div class="cartTableRow">
          <div class="cartTableRowColumn handle" style="cursor:pointer" v-if="!myFunctions.isMobile.value">
            <i class="pi pi-bars" style="font-size: 12px"></i>
          </div>
          <div class="cartTableRowColumn">
            <Button class="remove-button" icon="pi pi-trash" text rounded aria-label="Filter" @click="removeProduct(index)"></Button>
          </div>
          <div class="cartTableRowColumn">
            <Image class="image" v-if="product.product.images" :src="product.product.images[0].url" preview></Image>
          </div>
          <div class="cartTableRowColumn" style="padding:0 20px;">
            <div  @click="router.push('/shop/product/' + product.product.slug)"
                  style="text-decoration:underline;cursor:pointer;">
              {{product.product.Name}}
            </div>
            <InputText v-if="event" style="margin-top:5px" placeholder="Kommentar" size="small" v-model="product.comment"></InputText>
          </div>
          <div class="cartTableRowColumn">
            <InputNumber @click.stop="updateCart()" v-model="product.count" :max-fraction-digits="1" locale="de-DE" showButtons buttonLayout="stacked"
                         decrementButtonClass="p-button-text"
                         incrementButtonClass="p-button-text"
                         :min="0"
            />
          </div>
          <div class="cartTableRowColumn"  v-if="!myFunctions.isMobile.value">
            <template v-if="isPriceOfProductVisible(product.product)">
              <div v-if="!event">
                {{product.price.toLocaleString("de-AT",{style:"currency", currency:"EUR"})}}
              </div>
              <InputNumber v-else locale="de" suffix=" €" style="width:100%" :minFractionDigits="2" v-model="product.price"></InputNumber>
              <div v-if="event">Factor:</div>
              <InputNumber v-if="event" locale="de" style="width:100%" :minFractionDigits="1" v-model="product.factor"></InputNumber>
              <div v-else-if="product.factor > 1">Faktor {{ product.factor.toLocaleString("de-at") }}x</div>
            </template>
          </div>
          <div class="cartTableRowColumn">
            <div>
              <template  v-if="isPriceOfProductVisible(product.product)">
                {{(product.price * product.count * product.factor).toLocaleString("de-AT",{style:"currency", currency:"EUR"})}}
              </template>
              <template v-else>
                Auf Anfrage
              </template>
            </div>
          </div>
        </div>
       </div>
      </template>
    </draggable>
    <div v-if="event">
      <Button v-if="!showAddProductRow" @click="addProduct()" outlined>hinzufügen</Button>
      <div v-else style="display: flex;align-items:center;">
        <AutoComplete v-model="newProduct.Name" @complete="loadAutocompleteProducts()" :suggestions="productList" optionLabel="Name" @item-select="setProduct"/>
        <Button @click="saveNewProduct()" style="margin: 0 10px 0 0;">Speichern</Button>
        <Button @click="cancelAddProduct()" severity="danger" outlined>Abbrechen</Button>
      </div>
    </div>
    <div class="cartTableFooter">
      <template v-if="containsProductWithInvisiblePrice">
        <div class="cartTableFooterRow">
          <div style="width: 300px;">Anfrage enthält Produkte bei denen der Preis nur auf Anfrage verfügbar ist.</div>
        </div>
      </template>
      <template v-else>
        <div class="cartTableFooterRow">
          <div>Gesamt</div>
          <div>{{getTotalPrice().toLocaleString("de-DE", {style:"currency", currency:"EUR"})}}</div>
        </div>
        <div class="cartTableFooterRow" v-if="properties.event?.discount">
          <div>Gesamt nach Rabatt ({{properties.event?.discount}}%)</div>
          <div>{{properties.event.getNettoSumDiscounted().toLocaleString("de-DE", {style:"currency", currency:"EUR"})}}</div>
        </div>
        <div class="cartTableFooterRow" v-if="!event?.innergemeinschaftlich">
          <div>Gesamt inkl. MwSt.</div>
          <div>{{getTotalInclTax().toLocaleString("de-DE", {style:"currency", currency:"EUR"})}}</div>
        </div>
      </template>
    </div>
  </div>
</template>
<style lang="scss" scoped>

.cartTable{
  margin-top: 20px;
  width: 100%;
  .cartTableHeader{
    display: grid;
    grid-template-columns: 16px 36px 60px auto 100px 150px 100px;
    height: 28px;
    background: transparent;
    align-items: center;
  }
  .sectionName{
    width:100%;
    &.background{
      background: rgba(0, 0, 0, 0.49);
    }
  }
  .cartTableRow{
    display: grid;
    grid-template-columns: 36px 60px auto 100px 100px;
    align-items: center;
    padding: 10px 0;
    border:solid;
    border-width: 0.1px 0;
    border-color: $myGrey;
    @include tablet(){
      grid-template-columns: 16px 36px 60px auto 100px 150px 100px;
    }
    div{
      padding: 0 10px;
    }
    :deep(.p-image){
      background: $myGreyTransparent;
      padding:10px;
      height: 60px;
      width: 60px;
      img{
        width:100%;
        height:100%;
        object-fit: contain;
      }
    }

    :deep(.p-inputnumber){
      .p-inputtext{
        width:80px;
      }
    }
  }

  .cartTableFooter{
    .cartTableFooterRow{
      display:flex;
      justify-content: end;
      div{
        padding:5px 5px;
      }
    }
  }
}
.p-datatable{
  width:100%;
  background:unset;
  :deep(.p-image){
    background: $myGreyTransparent;
    padding:10px;
    height: 60px;
    width: 60px;
    img{
      width:100%;
      height:100%;
      object-fit: contain;
    }

  }
}
</style>